import translate from '../../core/translate'
import Content from '../../blocks.app/content/content'
import React from 'react'
import styles from './notificationСenter.jcss'
import Icon from '../../blocks.simple/icon/icon'
import { cn } from 'ethcss'
import { INotification, NotificationСenterHooks } from './NotificationСenterHooks'
import moment from 'moment'
import ExportHistory from 'pages/history/history'
import NotificationFilter from './__filter/notificationFilter__filter'
import { NotificationEmails } from './notificationEmails/NotificationEmails'
import { useDispatch } from 'react-redux'
import { userActions } from 'blocks.app/user/user.state'
import filesStyles from 'pages/files/files.jcss'
import helpers from 'core/helpers'
import Checkbox from '../../blocks.simple/checkbox/checkbox'
import InfiniteScroll from 'react-infinite-scroller'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import { TabList } from 'organisms/TabList'
import { EventDetails, IEventDetailsField } from 'organisms/EventDetails'
import { NotificationFilterList } from './__filterList/NotificationFilterList'
import { Button } from 'molecules/Button'
import { changeLocation, routes } from '../../features/routes'

export const NotificationCenter = (p_: { location: Location }) => {
    const {
        state,
        setState,
        filterFields,
        modelText,
        isDeletePermission,
        onSelect,
        onSelectAll,
        deleteSelectedNotifications,
        readSelected,
        getAllNotifications,
        getNotifications,
        getKeepDurationSettings,
    } = NotificationСenterHooks(p_.location)

    const dispatch = useDispatch()

    const goToSettings = () => {
        changeLocation(`/${routes.settings.path}?open=notifications`)
    }

    function renderTextWithFileLink(text: string, fileURL?: string) {
        if (!fileURL || !text.includes(fileURL)) return text

        const cleaned = text.replace(fileURL, '')

        return (
            <>
                {cleaned}
                <a href={fileURL} target="_blank" rel="noopener noreferrer">
                    {fileURL}
                </a>
            </>
        )
    }

    const openNotification = (notification: INotification) => {
        setState((prevState) => ({
            ...prevState,
            activeModalId: notification.id,
            isModalActive: true,
        }))
    }

    const closeNotification = () => {
        setState((prevState) => ({
            ...prevState,
            activeModalId: null,
            isModalActive: false,
            notificationInModal: null,
        }))
    }

    const getNotificationStatusColor = (type: string) => {
        switch (type) {
            case 'error':
                return '#EB5757'
            case 'warning':
                return '#F2C94C'
            default:
                return '#2D9CDB'
        }
    }

    const getNotificationDetails = (notification: INotification): IEventDetailsField[][] => {
        const name = modelText(notification, 'name')

        return [
            [
                {
                    label: translate('messageDateTime'),
                    value: moment(notification.createdAt).format('YYYY.MM.DD - HH:mm'),
                },
            ],
            [
                {
                    label: translate('category'),
                    value: translate(notification.category),
                },
                {
                    label: translate('eventStatus'),
                    value: translate(notification.type),
                    accentColor: getNotificationStatusColor(notification.type),
                },
            ],
            [
                {
                    label: translate('messageEventName'),
                    value: modelText(notification, 'title'),
                },
                ...(name !== '-'
                    ? [
                          {
                              label: translate('name'),
                              value: name,
                          },
                      ]
                    : []),
            ],
            [
                {
                    label: translate('message'),
                    value:
                        modelText(notification, 'text') === translate('passwordUpdate') ? (
                            <button
                                type="button"
                                className={filesStyles.link}
                                onClick={() => dispatch(userActions.openUserEditPanel())}
                            >
                                {translate('changeUserPassword')}
                            </button>
                        ) : (
                            renderTextWithFileLink(
                                modelText(notification, 'text'),
                                notification.context?.instance?.fileURL
                            )
                        ),
                },
            ],
        ]
    }

    const renderNotifications = () => (
        <Content title="eventJournal" wrapperClassName={styles.contentWrapper}>
            <div id="notification-page-notifications" role="tabpanel" aria-labelledby="tab-notificationCenter">
                <div className={styles.retentionRow}>
                    <div className={styles.caption}>
                        {translate('messageKeepDuration')}:{' '}
                        {getKeepDurationSettings('systemNotificationLifespanD', '30')}
                        <div className={styles.changeSettings} onClick={goToSettings}>
                            {translate('editLabel')}
                        </div>
                    </div>
                </div>
                <div className={styles.toolbarRow}>
                    <NotificationFilter filterFields={filterFields} setState={setState} state={state} />
                    <div className={styles.group}>
                        {state.selectedNotifications.length > 0 && (
                            <Button mod={'fillBrand'} onClick={() => readSelected()}>
                                {translate('makeAsRead')}
                            </Button>
                        )}
                        <Button mod={'fillBrand'} onClick={() => getAllNotifications()}>
                            {translate('getAll')}
                        </Button>
                    </div>
                </div>
                <NotificationFilterList filters={filterFields} />
                <div className={styles.table}>
                    <div className={cn(styles.tableGrid, styles.tableHeader)} role="row">
                        <div className={styles.checkboxCell} role="columnheader">
                            <Checkbox
                                onClick={(selected: boolean) => onSelectAll(selected)}
                                checked={
                                    state.notifications.length > 0 &&
                                    state.selectedNotifications.length === state.notifications.length
                                }
                            />
                        </div>
                        <div role="columnheader">{translate('session')}</div>
                        <div role="columnheader">{translate('eventStatus')}</div>
                        <div role="columnheader">{translate('event')}</div>
                        <div className={styles.rowAction} role="columnheader">
                            {helpers.isAvailable({ key: 'notificationCenter', action: 'delete' }) && (
                                <Icon
                                    disabled={!isDeletePermission || !state.selectedNotifications.length}
                                    type={'delete'}
                                    size={16}
                                    onClick={() => deleteSelectedNotifications()}
                                    tooltip={{ title: translate('delete') }}
                                />
                            )}
                        </div>
                    </div>
                    <InfiniteScroll
                        loadMore={() => getNotifications(true, true)}
                        hasMore={!!state.token}
                        initialLoad={false}
                        loader={<LoaderLazy key="notification-page-loader" active={true} />}
                        threshold={10}
                        useWindow={true}
                    >
                        {state.notifications.map((notification: INotification) => (
                            <div
                                key={notification.id}
                                className={cn(styles.tableGrid, styles.tableRow, {
                                    [styles.unreadRow]: notification.status === 'unread',
                                })}
                                role="row"
                            >
                                <div className={styles.checkboxCell} role="cell">
                                    <Checkbox
                                        onClick={(selected: boolean) => onSelect(notification.id, selected)}
                                        checked={state.selectedNotifications.includes(notification.id)}
                                    />
                                </div>
                                <div className={styles.dateCell} role="cell">
                                    <span>{moment(notification.createdAt).format('DD.MM.YYYY')}</span>
                                    <span>{moment(notification.createdAt).format('HH:mm:ss')}</span>
                                </div>
                                <div
                                    className={cn(styles.statusCell, {
                                        [styles.statusError]: notification.type === 'error',
                                        [styles.statusWarning]: notification.type === 'warning',
                                    })}
                                    role="cell"
                                >
                                    {translate(notification.type)}
                                </div>
                                <div className={styles.eventCell} role="cell">
                                    <span className={styles.eventTitle}>{modelText(notification, 'title')}</span>
                                    {modelText(notification, 'name') !== '-' && (
                                        <span className={styles.eventMeta}>
                                            {translate('name')}: {modelText(notification, 'name')}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className={styles.detailsButton}
                                    aria-label={translate('moreDetails')}
                                    onClick={() => openNotification(notification)}
                                >
                                    <Icon type={'arrow_right'} size={16} />
                                </button>
                            </div>
                        ))}
                    </InfiniteScroll>
                    {!state.notifications.length && <div className={styles.emptyState}>{translate('noNewEvents')}</div>}
                </div>
            </div>
        </Content>
    )

    const canReadUserHistory = helpers.isAvailable({ key: 'userHistory', action: 'read' })
    const renderContent = (tab: string) => {
        switch (tab) {
            case 'notificationCenter':
                return renderNotifications()
            case 'usersActionsHistory':
                return (
                    <div id="notification-page-history" role="tabpanel" aria-labelledby="tab-usersActionsHistory">
                        {canReadUserHistory && (
                            <ExportHistory
                                goToSettings={goToSettings}
                                location={p_.location}
                                getKeepDurationSettings={getKeepDurationSettings}
                            />
                        )}
                    </div>
                )
            case 'notificationEmails':
                return (
                    <div id="notification-page-mail" role="tabpanel" aria-labelledby="tab-notificationEmails">
                        <NotificationEmails
                            goToSettings={goToSettings}
                            getKeepDurationSettings={getKeepDurationSettings}
                        />
                    </div>
                )
            default:
                return null
        }
    }

    const availableTabs = state.tabs
        .filter((tab) => helpers.isAvailable({ key: tab.permission, action: 'read' }))
        .map((tab) => ({
            id: tab.name,
            label: translate(tab.name),
            controls:
                tab.name === 'notificationCenter'
                    ? 'notification-page-notifications'
                    : tab.name === 'usersActionsHistory'
                    ? 'notification-page-history'
                    : 'notification-page-mail',
        }))
    const firstAvailableTabId = availableTabs.length ? availableTabs[0].id : null
    const selectedTabIsAvailable = availableTabs.some((tab) => tab.id === state.selectedTab)

    React.useEffect(() => {
        if (firstAvailableTabId && !selectedTabIsAvailable) {
            setState((prevState) => ({
                ...prevState,
                selectedTab: firstAvailableTabId,
            }))
        }
    }, [firstAvailableTabId, selectedTabIsAvailable, state.selectedTab])

    return (
        <div>
            <Content title="eventJournal" wrapperClassName={styles.tabsWrapper}>
                <div className={styles.tabsSurface}>
                    <TabList
                        tabs={availableTabs}
                        activeTab={state.selectedTab}
                        ariaLabel={translate('eventJournal')}
                        onChange={(selectedTab) =>
                            setState((prevState) => ({
                                ...prevState,
                                selectedTab,
                            }))
                        }
                    />
                </div>
            </Content>
            {renderContent(state.selectedTab)}
            {state.isModalActive && state.notificationInModal && (
                <EventDetails
                    title={translate('eventDetails')}
                    backLabel={translate('back')}
                    groups={getNotificationDetails(state.notificationInModal)}
                    onBack={closeNotification}
                />
            )}
        </div>
    )
}
